// export default function POS() {
//   return <div>POS Settings Section</div>;
// }

import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Stack,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useAuth } from "../../context/AuthContext";
import { isPosEnabledForStore } from "../../utils/subsystemAccess";
import {
  getPosStoreSettings,
  savePosStoreSettings,
  uploadPosStoreAsset,
  deletePosStoreAsset,
} from "../../services/posSettingsService";

const MAX_STORE_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_STORE_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

function StorePhotoSlot({
  title,
  emptyLabel,
  url,
  loading,
  onSelectFile,
  onRemove,
  theme,
}) {
  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>
      <Box
        sx={{
          height: 180,
          borderRadius: 2,
          border: `1px dashed ${theme.palette.divider}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          bgcolor: theme.palette.action.hover,
          mb: 1.5,
          position: "relative",
        }}
      >
        {url ? (
          <Box
            component="img"
            src={url}
            alt={title}
            sx={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {emptyLabel}
          </Typography>
        )}
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(0,0,0,0.28)",
            }}
          >
            <CircularProgress size={28} />
          </Box>
        )}
      </Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <Button variant="outlined" component="label" disabled={loading} size="small">
          {url ? "Replace" : "Upload"}
          <input
            hidden
            type="file"
            accept="image/png, image/jpeg, image/webp"
            onChange={onSelectFile}
          />
        </Button>
        {url && (
          <Button color="error" size="small" onClick={onRemove} disabled={loading}>
            Remove
          </Button>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Max size 5 MB. JPG, PNG, or WebP.
      </Typography>
    </Box>
  );
}

export default function POS() {
  const muiTheme = useTheme();
  const { user } = useAuth();
  const posEnabled = isPosEnabledForStore(user);

  const [storeDetails, setStoreDetails] = useState({
    storeName: "",
    abn: "",
    storeType: "",
    storeEmail: "",
    storePhone: "",
    storeAddress: "",
    businessPhone: "",
    businessEmail: "",
    managerName: "",
    managerContact: "",
    supportContact: "",
    currency: "",
    taxSettings: "",
    receiptHeaderText: "",
    receiptFooterText: "",
    serviceChargePercent: "",
    tableServiceEnabled: false,
    onlineOrderingEnabled: false,
    storeStatus: "active",
    notes: "",
    logoUrl: "",
    menuUrl: "",
    createdAt: null,
    updatedAt: null,
    createdBy: null,
  });
  const [photoLoading, setPhotoLoading] = useState({
    logo: false,
    menu: false,
  });
  const [toast, setToast] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  // Load existing settings on mount
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const settings = await getPosStoreSettings();
        if (!isMounted) return;

        if (settings) {
          setStoreDetails({
            storeName: settings.store_name || "",
            abn: settings.abn || "",
            storeType: settings.store_type || "",
            storeEmail: settings.store_email || "",
            storePhone: settings.store_phone || "",
            storeAddress: settings.store_address || "",
            businessPhone: settings.business_phone || "",
            businessEmail:
              settings.business_email || user?.email || "",
            managerName: settings.manager_name || "",
            managerContact: settings.manager_contact || "",
            supportContact: settings.support_contact || "",
            currency: settings.currency || "",
            taxSettings: settings.tax_settings || "",
            receiptHeaderText: settings.receipt_header_text || "",
            receiptFooterText: settings.receipt_footer_text || "",
            serviceChargePercent:
              settings.service_charge_percent ?? "",
            tableServiceEnabled: !!settings.table_service_enabled,
            onlineOrderingEnabled: !!settings.online_ordering_enabled,
            storeStatus: settings.store_status || "active",
            notes: settings.notes || "",
            logoUrl: settings.logo_url || "",
            menuUrl: settings.menu_url || "",
            createdAt: settings.created_at || null,
            updatedAt: settings.updated_at || null,
            createdBy: settings.created_by || null,
          });
        } else {
          // Seed with sensible defaults
          setStoreDetails((prev) => ({
            ...prev,
            storeName: prev.storeName || user?.companies?.name || "",
            businessEmail: prev.businessEmail || user?.email || "",
          }));
        }
      } catch (e) {
        // For now, fail silently and just show defaults
        // eslint-disable-next-line no-console
        console.error("Failed to load POS store settings", e);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [user]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setStoreDetails((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    const payload = {
      storeName: storeDetails.storeName,
      abn: storeDetails.abn,
      storeType: storeDetails.storeType,
      storeEmail: storeDetails.storeEmail,
      storePhone: storeDetails.storePhone,
      storeAddress: storeDetails.storeAddress,
      businessPhone: storeDetails.businessPhone,
      businessEmail: storeDetails.businessEmail,
      managerName: storeDetails.managerName,
      managerContact: storeDetails.managerContact,
      supportContact: storeDetails.supportContact,
      currency: storeDetails.currency,
      taxSettings: storeDetails.taxSettings,
      receiptHeaderText: storeDetails.receiptHeaderText,
      receiptFooterText: storeDetails.receiptFooterText,
      serviceChargePercent:
        storeDetails.serviceChargePercent === ""
          ? null
          : Number(storeDetails.serviceChargePercent),
      tableServiceEnabled: storeDetails.tableServiceEnabled,
      onlineOrderingEnabled: storeDetails.onlineOrderingEnabled,
      storeStatus: storeDetails.storeStatus,
      notes: storeDetails.notes,
    };

    try {
      const saved = await savePosStoreSettings(payload);
      if (saved) {
        setStoreDetails((prev) => ({
          ...prev,
          storeName: saved.store_name || prev.storeName,
          abn: saved.abn || prev.abn,
          storeType: saved.store_type || prev.storeType,
          storeEmail: saved.store_email || prev.storeEmail,
          storePhone: saved.store_phone || prev.storePhone,
          storeAddress: saved.store_address || prev.storeAddress,
          businessPhone: saved.business_phone || prev.businessPhone,
          businessEmail: saved.business_email || prev.businessEmail,
          managerName: saved.manager_name || prev.managerName,
          managerContact: saved.manager_contact || prev.managerContact,
          supportContact: saved.support_contact || prev.supportContact,
          currency: saved.currency || prev.currency,
          taxSettings: saved.tax_settings || prev.taxSettings,
          receiptHeaderText:
            saved.receipt_header_text || prev.receiptHeaderText,
          receiptFooterText:
            saved.receipt_footer_text || prev.receiptFooterText,
          serviceChargePercent:
            saved.service_charge_percent ?? prev.serviceChargePercent,
          tableServiceEnabled:
            saved.table_service_enabled ?? prev.tableServiceEnabled,
          onlineOrderingEnabled:
            saved.online_ordering_enabled ?? prev.onlineOrderingEnabled,
          storeStatus: saved.store_status ?? prev.storeStatus,
          notes: saved.notes ?? prev.notes,
          logoUrl: saved.logo_url ?? prev.logoUrl,
          menuUrl: saved.menu_url ?? prev.menuUrl,
          createdAt: saved.created_at ?? prev.createdAt,
          updatedAt: saved.updated_at ?? prev.updatedAt,
          createdBy: saved.created_by ?? prev.createdBy,
        }));

        setToast({
          open: true,
          severity: "success",
          message: "Store settings saved",
        });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Failed to save POS store settings", e);
      setToast({
        open: true,
        severity: "error",
        message: e?.message || "Failed to save store settings",
      });
    }
  };

  const applyAssetSettings = (saved) => {
    if (!saved) return;
    setStoreDetails((prev) => ({
      ...prev,
      logoUrl: Object.prototype.hasOwnProperty.call(saved, "logo_url")
        ? saved.logo_url || ""
        : prev.logoUrl,
      menuUrl: Object.prototype.hasOwnProperty.call(saved, "menu_url")
        ? saved.menu_url || ""
        : prev.menuUrl,
      updatedAt: saved.updated_at ?? prev.updatedAt,
    }));
  };

  const handlePhotoSelect = (kind) => async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!ALLOWED_STORE_IMAGE_TYPES.includes(file.type)) {
      setToast({
        open: true,
        severity: "error",
        message: "Only JPG, PNG, and WebP images are allowed",
      });
      return;
    }

    if (file.size > MAX_STORE_IMAGE_SIZE) {
      setToast({
        open: true,
        severity: "error",
        message: "File is too large. Maximum size is 5 MB.",
      });
      return;
    }

    setPhotoLoading((prev) => ({ ...prev, [kind]: true }));
    try {
      const saved = await uploadPosStoreAsset(kind, file);
      applyAssetSettings(saved);
      setToast({
        open: true,
        severity: "success",
        message: kind === "logo" ? "Store logo uploaded" : "Store menu uploaded",
      });
    } catch (e) {
      setToast({
        open: true,
        severity: "error",
        message: e?.message || `Failed to upload store ${kind}`,
      });
    } finally {
      setPhotoLoading((prev) => ({ ...prev, [kind]: false }));
    }
  };

  const handlePhotoRemove = (kind) => async () => {
    setPhotoLoading((prev) => ({ ...prev, [kind]: true }));
    try {
      const saved = await deletePosStoreAsset(kind);
      applyAssetSettings(saved);
      setToast({
        open: true,
        severity: "success",
        message: kind === "logo" ? "Store logo removed" : "Store menu removed",
      });
    } catch (e) {
      setToast({
        open: true,
        severity: "error",
        message: e?.message || `Failed to remove store ${kind}`,
      });
    } finally {
      setPhotoLoading((prev) => ({ ...prev, [kind]: false }));
    }
  };

  return (
    <Box
      sx={{
        px: 4,
        pt: 0,
        pb: 4,
        maxWidth: 1040,
        mx: "auto",
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            letterSpacing: "-0.4px",
            color: muiTheme.palette.text.primary,
          }}
        >
          Store Details
        </Typography>
        <Typography
          variant="body2"
          sx={{ mt: 0.5, color: muiTheme.palette.text.secondary }}
        >
          Manage your store information, contacts, and operational settings.
        </Typography>
      </Box>

      <Stack spacing={3}>
        <Card
          sx={{
            borderRadius: 3,
            border: `1px solid ${muiTheme.palette.divider}`,
          }}
        >
          <CardContent>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2.5,
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, letterSpacing: "-0.2px" }}
                >
                  Store Details
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mt: 0.5, color: muiTheme.palette.text.secondary }}
                >
                  This information will be used across POS, receipts, and staff
                  tools.
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Store Name"
                  placeholder="e.g. Envoy Cafe - CBD"
                  value={storeDetails.storeName}
                  onChange={handleChange("storeName")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="ABN"
                  placeholder="e.g. 12 345 678 901"
                  value={storeDetails.abn}
                  onChange={handleChange("abn")}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Store Type"
                  placeholder="Select store type"
                  value={storeDetails.storeType}
                  onChange={handleChange("storeType")}
                >
                  <MenuItem value="cafe">Cafe</MenuItem>
                  <MenuItem value="restaurant">Restaurant</MenuItem>
                  <MenuItem value="bar">Bar</MenuItem>
                  <MenuItem value="quick_service">Quick Service</MenuItem>
                  <MenuItem value="retail">Retail</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Store Email"
                  type="email"
                  placeholder="store@example.com"
                  value={storeDetails.storeEmail}
                  onChange={handleChange("storeEmail")}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Store Phone"
                  placeholder="+61 4xx xxx xxx"
                  value={storeDetails.storePhone}
                  onChange={handleChange("storePhone")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Business Phone"
                  placeholder="+61 2 xxx xxxx"
                  value={storeDetails.businessPhone}
                  onChange={handleChange("businessPhone")}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Store Address"
                  multiline
                  minRows={2}
                  placeholder="Street address, suburb, state, postcode"
                  value={storeDetails.storeAddress}
                  onChange={handleChange("storeAddress")}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1.5 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Business Email"
                  type="email"
                  placeholder="accounts@example.com"
                  value={storeDetails.businessEmail}
                  onChange={handleChange("businessEmail")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Support Contact"
                  placeholder="support phone or email"
                  value={storeDetails.supportContact}
                  onChange={handleChange("supportContact")}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Manager Name"
                  placeholder="e.g. Alex Smith"
                  value={storeDetails.managerName}
                  onChange={handleChange("managerName")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Manager Contact"
                  placeholder="phone or email"
                  value={storeDetails.managerContact}
                  onChange={handleChange("managerContact")}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {posEnabled && (
        <Card
          sx={{
            borderRadius: 3,
            border: `1px solid ${muiTheme.palette.divider}`,
          }}
        >
          <CardContent>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2.5,
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, letterSpacing: "-0.2px" }}
                >
                  POS Configuration
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mt: 0.5, color: muiTheme.palette.text.secondary }}
                >
                  Settings used by POS devices.
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Currency"
                  value={storeDetails.currency}
                  onChange={handleChange("currency")}
                >
                  <MenuItem value="AUD">AUD</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                  <MenuItem value="NZD">NZD</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Tax Settings"
                  value={storeDetails.taxSettings}
                  onChange={handleChange("taxSettings")}
                >
                  <MenuItem value="inclusive">Tax inclusive</MenuItem>
                  <MenuItem value="exclusive">Tax exclusive</MenuItem>
                  <MenuItem value="none">No tax</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Receipt Header Text"
                  multiline
                  minRows={2}
                  value={storeDetails.receiptHeaderText}
                  onChange={handleChange("receiptHeaderText")}
                  placeholder="Optional message printed at the top of receipts"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Receipt Footer Text"
                  multiline
                  minRows={2}
                  value={storeDetails.receiptFooterText}
                  onChange={handleChange("receiptFooterText")}
                  placeholder="Thank you message, legal text, etc."
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Service Charge %"
                  value={storeDetails.serviceChargePercent}
                  onChange={handleChange("serviceChargePercent")}
                  inputProps={{ min: 0, step: 0.1 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack
                  direction="row"
                  spacing={3}
                  alignItems="center"
                  justifyContent="flex-start"
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">Table service</Typography>
                    <Button
                      variant={
                        storeDetails.tableServiceEnabled
                          ? "contained"
                          : "outlined"
                      }
                      size="small"
                      onClick={() =>
                        setStoreDetails((prev) => ({
                          ...prev,
                          tableServiceEnabled: !prev.tableServiceEnabled,
                        }))
                      }
                    >
                      {storeDetails.tableServiceEnabled ? "Enabled" : "Disabled"}
                    </Button>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">Online ordering</Typography>
                    <Button
                      variant={
                        storeDetails.onlineOrderingEnabled
                          ? "contained"
                          : "outlined"
                      }
                      size="small"
                      onClick={() =>
                        setStoreDetails((prev) => ({
                          ...prev,
                          onlineOrderingEnabled:
                            !prev.onlineOrderingEnabled,
                        }))
                      }
                    >
                      {storeDetails.onlineOrderingEnabled
                        ? "Enabled"
                        : "Disabled"}
                    </Button>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        )}

        {/* Store Status & Audit */}
        <Card
          sx={{
            borderRadius: 3,
            border: `1px solid ${muiTheme.palette.divider}`,
          }}
        >
          <CardContent>
            <Box sx={{ mb: 2.5 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, letterSpacing: "-0.2px" }}
              >
                Store Status
              </Typography>
              <Typography
                variant="body2"
                sx={{ mt: 0.5, color: muiTheme.palette.text.secondary }}
              >
                Status and audit information for this store.
              </Typography>
            </Box>

            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Store Status"
                  value={storeDetails.storeStatus}
                  onChange={handleChange("storeStatus")}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} />

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Created At"
                  value={
                    storeDetails.createdAt
                      ? new Date(storeDetails.createdAt).toLocaleString()
                      : "—"
                  }
                  InputProps={{ readOnly: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Updated At"
                  value={
                    storeDetails.updatedAt
                      ? new Date(storeDetails.updatedAt).toLocaleString()
                      : "—"
                  }
                  InputProps={{ readOnly: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Created By"
                  value={storeDetails.createdBy || "—"}
                  InputProps={{ readOnly: true }}
                  size="small"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes / Internal Comments"
                  multiline
                  minRows={3}
                  value={storeDetails.notes}
                  onChange={handleChange("notes")}
                  placeholder="Internal notes about this store"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card
          sx={{
            borderRadius: 3,
            border: `1px solid ${muiTheme.palette.divider}`,
          }}
        >
          <CardContent>
            <Box sx={{ mb: 2.5 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, letterSpacing: "-0.2px" }}
              >
                Store Photos
              </Typography>
              <Typography
                variant="body2"
                sx={{ mt: 0.5, color: muiTheme.palette.text.secondary }}
              >
                Upload your store logo and a photo of the menu. These save as soon
                as you upload them.
              </Typography>
            </Box>

            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <StorePhotoSlot
                  title="Store logo"
                  emptyLabel="No logo yet"
                  url={storeDetails.logoUrl}
                  loading={photoLoading.logo}
                  onSelectFile={handlePhotoSelect("logo")}
                  onRemove={handlePhotoRemove("logo")}
                  theme={muiTheme}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <StorePhotoSlot
                  title="Store menu"
                  emptyLabel="No menu photo yet"
                  url={storeDetails.menuUrl}
                  loading={photoLoading.menu}
                  onSelectFile={handlePhotoSelect("menu")}
                  onRemove={handlePhotoRemove("menu")}
                  theme={muiTheme}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={handleSave}
            sx={{
              px: 3.5,
              fontWeight: 700,
              boxShadow: "0 10px 26px rgba(37,99,235,0.25)",
            }}
          >
            Save Store Settings
          </Button>
        </Box>
      </Stack>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={(_, reason) => {
          if (reason === "clickaway") return;
          setToast((prev) => ({ ...prev, open: false }));
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() =>
            setToast((prev) => ({
              ...prev,
              open: false,
            }))
          }
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}